import { Viewport } from 'common/enums/Viewport';
import { useMatchesViewport } from 'common/hooks/media';

/**
 * @TODO Code refactoring needed.
 * We should consider a refactoring of this logic involving these `SLIDES_TO_SHOW_*`
 * and `SLIDE_WIDTH_*` variables. This logic needs to be revisited as it's no longer accurate.
 * For example: Right below, we have `SLIDES_TO_SHOW_XL` with the value 5, assuming 5 slides
 * should be shown in the Extra Large (XL) breakpoint. However, 6 and a half slides are
 * actually shown in the component.
 */
export const SLIDES_TO_SHOW_XL = 5;
const SLIDES_TO_SHOW_L = 4;
const SLIDES_TO_SHOW_M = 3;
export const SLIDES_TO_SHOW_S = 2;

const SLIDE_WIDTH_LARGE_DESKTOP = 0.85;
const SLIDE_WIDTH_DESKTOP = 0.85;
const SLIDE_WIDTH_TABLET = 0.72;
export const SLIDE_WIDTH_MOBILE = 0.81;

const SLIDE_WIDTH_LARGE_DESKTOP_WITH_BG = 1.05;
const SLIDE_WIDTH_DESKTOP_WITH_BG = 0.84;
const SLIDE_WIDTH_TABLET_WITH_BG = 0.79;
export const SLIDE_WIDTH_MOBILE_WITH_BG = 0.92;

export const SLIDE_FALLBACK_WIDTH = [
  SLIDE_WIDTH_MOBILE / SLIDES_TO_SHOW_S,
  SLIDE_WIDTH_TABLET / SLIDES_TO_SHOW_M,
  SLIDE_WIDTH_DESKTOP / SLIDES_TO_SHOW_L,
  SLIDE_WIDTH_LARGE_DESKTOP / SLIDES_TO_SHOW_XL,
];

export const SLIDE_FALLBACK_WIDTH_WITH_BG = [
  SLIDE_WIDTH_MOBILE_WITH_BG / SLIDES_TO_SHOW_S,
  SLIDE_WIDTH_TABLET_WITH_BG / SLIDES_TO_SHOW_M,
  SLIDE_WIDTH_DESKTOP_WITH_BG / SLIDES_TO_SHOW_L,
  SLIDE_WIDTH_LARGE_DESKTOP_WITH_BG / SLIDES_TO_SHOW_XL,
];

/**
 * In order to show always, for each breakpoint, a defined number of slides, we have
 * to manually set the slideWidth, to overwrite the default behaviour (=> slider-width / slides-total-number),
 * and the slidesToShow, to define the number of slides we want to show at once.
 * WARNING: If the slideWidth that is passed to Carousel is
 * changed, please update SlideContainer width values
 */
const CAROUSEL_CONFIG = {
  [Viewport.LargeDesktop]: {
    slidesToShow: SLIDES_TO_SHOW_XL,
    slideWidth: SLIDE_WIDTH_LARGE_DESKTOP,
    frameOverflow: 'hidden',
    scrollToTheLastSlide: false,
  },
  [Viewport.Desktop]: {
    slidesToShow: SLIDES_TO_SHOW_L,
    slideWidth: SLIDE_WIDTH_DESKTOP,
    frameOverflow: 'visible',
    scrollToTheLastSlide: false,
  },
  [Viewport.Tablet]: {
    slidesToShow: SLIDES_TO_SHOW_M,
    slideWidth: SLIDE_WIDTH_TABLET,
    frameOverflow: 'visible',
    scrollToTheLastSlide: false,
  },
  [Viewport.Mobile]: {
    slidesToShow: SLIDES_TO_SHOW_S,
    slideWidth: SLIDE_WIDTH_MOBILE,
    frameOverflow: 'visible',
    scrollToTheLastSlide: true,
  },
};

const getConfig = (
  {
    matches: { matchesTablet, matchesDesktop, matchesLargeDesktop },
  }: ReturnType<typeof useMatchesViewport>,
  withBackground?: boolean
) => {
  switch (true) {
    case matchesLargeDesktop:
      return {
        ...CAROUSEL_CONFIG[Viewport.LargeDesktop],
        ...(withBackground && {
          slideWidth: SLIDE_WIDTH_LARGE_DESKTOP_WITH_BG,
        }),
      };
    case matchesDesktop:
      return {
        ...CAROUSEL_CONFIG[Viewport.Desktop],
        ...(withBackground && { slideWidth: SLIDE_WIDTH_DESKTOP_WITH_BG }),
      };
    case matchesTablet:
      return {
        ...CAROUSEL_CONFIG[Viewport.Tablet],
        ...(withBackground && { slideWidth: SLIDE_WIDTH_TABLET_WITH_BG }),
      };
    default:
      return {
        ...CAROUSEL_CONFIG[Viewport.Mobile],
        ...(withBackground && { slideWidth: SLIDE_WIDTH_MOBILE_WITH_BG }),
      };
  }
};

export const useCarouselConfig = (withBackground?: boolean, forceVisibleSlidesCount?: number) => {
  const matchesViewport = useMatchesViewport();
  const config = getConfig(matchesViewport, withBackground);

  if (forceVisibleSlidesCount) {
    return {
      ...config,
      slidesToShow: forceVisibleSlidesCount,
    };
  }

  return config;
};
